(function () {
  class GrCenterCarousel {
    
    constructor(root) {
      this.root = root;
      this.viewport = root.querySelector('.gr-center-carousel__viewport');
      this.track = root.querySelector('[data-track]');
      this.slides = Array.from(root.querySelectorAll('[data-slide]'));
      this.index = 0;
      this.pointerActive = false;
      this.dragging = false;
      this.moved = false;
      this.lockAxis =  (null);
      this.startX = 0;
      this.startY = 0;
      this.deltaX = 0;
      this.baseTranslate = 0;
      this.pointerId = null;

      if (!this.viewport || !this.track || !this.slides.length) return;

      const initial = Number(root.dataset.initialIndex || 0);
      this.index = Math.min(Math.max(initial, 0), this.slides.length - 1);

      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onResize = () => {
        if (!this.pointerActive) this.goTo(this.index, false);
      };

      root.querySelector('[data-prev]')?.addEventListener('click', (e) => {
        e.preventDefault();
        this.goTo(this.index - 1, true);
      });
      root.querySelector('[data-next]')?.addEventListener('click', (e) => {
        e.preventDefault();
        this.goTo(this.index + 1, true);
      });

      this.slides.forEach((slide) => {
        slide.addEventListener('click', (e) => this.onSlideClick(e, slide));
      });

      this.viewport.addEventListener('pointerdown', this.onPointerDown);
      window.addEventListener('resize', this.onResize);

      this.goTo(this.index, false);
      requestAnimationFrame(() => this.goTo(this.index, false));
      window.addEventListener('load', () => this.goTo(this.index, false), { once: true });
    }

    
    onSlideClick(e, slide) {
      e.preventDefault();
      e.stopPropagation();
      if (this.moved) return;

      const next = Number(slide.dataset.index);
      if (Number.isNaN(next)) return;

      if (next !== this.index) {
        this.goTo(next, true);
        return;
      }

      const url = slide.dataset.productUrl;
      if (url) {
        window.location.href = url;
      }
    }

    
    onPointerDown(e) {
      if (e.button !== 0) return;
      if (e.target instanceof Element && e.target.closest('.gr-center-carousel__nav')) return;

      this.pointerActive = true;
      this.dragging = false;
      this.moved = false;
      this.lockAxis = null;
      this.pointerId = e.pointerId;
      this.startX = e.clientX;
      this.startY = e.clientY;
      this.deltaX = 0;
      this.baseTranslate = this.readTranslate();

      this.viewport.addEventListener('pointermove', this.onPointerMove, { passive: false });
      this.viewport.addEventListener('pointerup', this.onPointerUp);
      this.viewport.addEventListener('pointercancel', this.onPointerUp);
    }

    
    onPointerMove(e) {
      if (!this.pointerActive || !this.track) return;
      if (this.pointerId !== null && e.pointerId !== this.pointerId) return;

      const dx = e.clientX - this.startX;
      const dy = e.clientY - this.startY;

      if (!this.lockAxis) {
        if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
        this.lockAxis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';

        if (this.lockAxis === 'y') {

          this.teardownPointer(false);
          return;
        }

        this.dragging = true;
        this.moved = true;
        this.root.classList.add('is-dragging');

        try {
          this.viewport.setPointerCapture(e.pointerId);
        } catch (_) {
          
        }
      }

      if (this.lockAxis !== 'x') return;

      e.preventDefault();
      e.stopPropagation();
      this.deltaX = dx;
      this.track.style.transform = `translate3d(${this.baseTranslate + dx}px, 0, 0)`;
    }

    onPointerUp() {
      if (!this.pointerActive && !this.dragging) return;

      const wasDragging = this.dragging;
      const deltaX = this.deltaX;
      this.teardownPointer(true);

      if (!wasDragging) return;

      const threshold = Math.min(64, Math.max(40, this.viewport.clientWidth * 0.1));
      if (deltaX > threshold) this.goTo(this.index - 1, true);
      else if (deltaX < -threshold) this.goTo(this.index + 1, true);
      else this.goTo(this.index, true);

      window.setTimeout(() => {
        this.moved = false;
      }, 80);
    }

    
    teardownPointer(releaseCapture = true) {
      this.pointerActive = false;
      this.dragging = false;
      this.lockAxis = null;
      this.root.classList.remove('is-dragging');

      this.viewport.removeEventListener('pointermove', this.onPointerMove);
      this.viewport.removeEventListener('pointerup', this.onPointerUp);
      this.viewport.removeEventListener('pointercancel', this.onPointerUp);

      if (releaseCapture && this.pointerId !== null) {
        try {
          this.viewport.releasePointerCapture(this.pointerId);
        } catch (_) {
          
        }
      }
      this.pointerId = null;
    }

    getMetrics() {
      const gap = parseFloat(getComputedStyle(this.track).gap) || 0;

      const probe = document.createElement('div');
      probe.setAttribute('aria-hidden', 'true');
      probe.style.cssText =
        'position:absolute;visibility:hidden;pointer-events:none;height:0;';
      this.root.appendChild(probe);

      probe.style.width = 'var(--gr-cc-side-w)';
      const side = probe.getBoundingClientRect().width || 140;

      probe.style.width = 'var(--gr-cc-center-w)';
      const center = probe.getBoundingClientRect().width || 259;

      probe.remove();

      return { side, center, gap };
    }

    
    translateFor(index) {
      if (!this.viewport || !this.slides.length) return 0;

      const { side, center, gap } = this.getMetrics();

      const offset = index * (side + gap);
      return this.viewport.clientWidth / 2 - (offset + center / 2);
    }

    
    goTo(index, animate) {
      if (!this.track || !this.viewport || !this.slides.length) return;
      if (this.viewport.clientWidth < 10) return;

      const total = this.slides.length;
      this.index = ((index % total) + total) % total;

      this.slides.forEach((slide, i) => {
        slide.classList.toggle('is-active', i === this.index);
      });

      const applyTranslate = () => {
        const translate = this.translateFor(this.index);
        this.track.style.transition = animate ? '' : 'none';
        this.track.style.transform = `translate3d(${translate}px, 0, 0)`;
        this.baseTranslate = translate;

        if (!animate) {
          void this.track.offsetWidth;
          this.track.style.transition = '';
        }
      };

      applyTranslate();
      if (animate) {
        window.setTimeout(applyTranslate, 360);
      } else {
        requestAnimationFrame(applyTranslate);
      }
    }

    readTranslate() {
      if (!this.track) return this.baseTranslate;
      const matrix = getComputedStyle(this.track).transform;
      if (!matrix || matrix === 'none') return this.baseTranslate;

      if (matrix.startsWith('matrix3d(')) {
        const parts = matrix
          .slice(9, -1)
          .split(',')
          .map((v) => Number(v.trim()));
        return Number.isFinite(parts[12]) ? parts[12] : this.baseTranslate;
      }

      const match = matrix.match(/matrix\(([^)]+)\)/);
      if (!match) return this.baseTranslate;
      const x = Number(match[1].split(',')[4]);
      return Number.isFinite(x) ? x : this.baseTranslate;
    }
  }

  function init() {
    document.querySelectorAll('[data-gr-center-carousel]').forEach((root) => {
      if (!(root instanceof HTMLElement) || root.dataset.grCcReady === 'true') return;
      root.dataset.grCcReady = 'true';
      new GrCenterCarousel(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', () => {
    document.querySelectorAll('[data-gr-center-carousel]').forEach((root) => {
      if (root instanceof HTMLElement) delete root.dataset.grCcReady;
    });
    init();
  });
})();
